import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { format, isValid } from "date-fns";
import { stringify } from "qs";
import { AButton } from "main/javascripts/components/button/AButton";
import { Cruise } from "main/javascripts/types/cruise";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { parseDate } from "main/javascripts/utils/DateUtil";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";

export interface IProps {
  uniqueId: string;
  schedule: Cruise.CruiseSchedule;
  hideDetailButton?: boolean;
  isEventTracking?: boolean;
}

export const CruiseScheduleListItem: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const {
    uniqueId,
    schedule,
    hideDetailButton = false,
    isEventTracking = false,
  } = props;

  const { t, i18n } = useTranslation(["component", "label", "unit"]);

  const formatDate = (dateString: string) => {
    const date = parseDate(dateString);
    if (isValid(date)) {
      return format(
        date,
        i18n.language === "ja" ? "yyyy/MM/dd" : "MMMM d, yyyy"
      );
    }
    return dateString;
  };

  const params = {
    departure_date: schedule.departureDate,
  };
  const paramsString = stringify(params);
  const showPagePath = `/cruises/${uniqueId}?${paramsString}`;

  const value = schedule.price;

  return (
    <div css={itemStyle}>
      <div css={dateStyle}>
        {t("cruise.departureOn", {
          date: formatDate(schedule.departureDate),
        })}
      </div>
      <div css={priceButtonBlockStyle}>
        <div css={priceStyle}>
          <Trans i18nKey="unit:priceStartsAt" values={{ value }}>
            from
            <span css={priceValueStyle}>{value}</span> p.p.
          </Trans>
        </div>
        {!hideDetailButton && (
          <div css={buttonBlockStyle}>
            <AButton
              type="button"
              styleKey="buttonSmall"
              href={showPagePath}
              target="_blank"
              rel="noopener"
              {...(isEventTracking
                ? {
                    "data-event-category":
                      GoogleTagManagerEventTypes.clickCruiseShow,
                  }
                : {})}
            >
              {t("label:common.showDetail")}
            </AButton>
          </div>
        )}
      </div>
    </div>
  );
};

const itemStyle = css`
  padding: ${space.atom2x};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space.atom} ${space.atom2_5x};
  border-top: 1px solid ${borderColor.primaryLightColor};

  &:first-of-type {
    border-top: 0;
  }

  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom1_5x} ${space.atom2_5x};
  }
`;
const dateStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.mediumHeadline2};
  min-width: 8rem;
  font-variant-numeric: tabular-nums;
  color: ${textColor.primaryDarkColor};
`;
const priceButtonBlockStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.atom} ${space.atom2_5x};
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  color: ${textColor.primaryDarkColor};
`;
const priceStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  min-width: 9rem;
  text-align: right;
`;
const priceValueStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.mediumHeadline2};
  font-variant-numeric: tabular-nums;
  font-weight: ${fontWeight.medium};
`;
const buttonBlockStyle = css`
  min-width: 8rem;
`;
